import * as React from 'react';
import cx from 'classnames';
import { env } from '@dx-ui/framework-env';
import { Button, Text, getButtonClasses } from '@dx-ui/global-ui-prism';
import { Icon } from '@dx-ui/osc-icon';
import { useTranslation } from 'next-i18next';
import type { Event } from '../../hooks/useEventData';

export type IHotel = {
  hotel: NonNullable<NonNullable<Event['event']>['info']>['ratePlans'][number]['hotel'];
};

export type IHotelInfo = {
  isPreviewView: boolean;
  isNoAvailError: boolean;
  isAvailBellCurveError?: boolean;
  bookURL?: string;
  hotelData: IHotel;
};

export const getPhoneNumber = (number?: string, country?: string) => {
  if (number) {
    return country === 'US'
      ? number.replace(/(\+\d{1})-(\d{3})-(\d{3})-(\d{4})/g, '$1 ($2) $3-$4')
      : number.replace(/-/g, ' ');
  }
  return number;
};

const isIntlAddress = (country?: string) => country !== 'US';

const HotelInfo: React.FC<React.PropsWithChildren<IHotelInfo>> = (props) => {
  const { t } = useTranslation();
  const hotelImage = {
    src: props?.hotelData?.hotel?.images?.master?.variants?.[0]?.url,
    altText: props?.hotelData?.hotel?.images?.master?.altText,
  };

  return (
    <div className="flex flex-1 flex-wrap gap-4 sm:flex-nowrap md:flex-col lg:flex-row">
      {hotelImage.src ? (
        <picture className="size-full sm:max-w-xs sm:flex-1">
          <img className="w-full" src={hotelImage.src} alt={hotelImage.altText || ''} />
        </picture>
      ) : (
        <section className="mb-4 flex size-full min-h-[11.625rem] max-w-[19.0625rem] flex-col items-center justify-center place-self-stretch bg-surface-legacy lg:mb-0 lg:mr-8 lg:max-h-[11.625rem] lg:max-w-[16.9375rem]">
          {t('textContent.noPhoto')}
        </section>
      )}
      <div className="flex w-full flex-col gap-4 sm:w-auto sm:flex-1">
        <div className="flex items-center">
          <picture className="me-2 shrink-0">
            <img
              className="inline-block h-7"
              alt={`${props?.hotelData?.hotel?.brand?.name} ${t('accessibility.logo')}`}
              src={`${env('HILTON_ASSETS_URI')}/modules/assets/svgs/logos/bug/${
                props?.hotelData?.hotel?.brandCode
              }.svg`}
            />
          </picture>
          <Text className="ms-1">{props?.hotelData?.hotel?.name}</Text>
        </div>
        <div className="flex">
          <Icon name="location" size="md" className="shrink-0" aria-hidden={true} />
          <p className="ms-3.5 flex flex-col">
            <span className="sr-only">{t('accessibility.address')}</span>
            {isIntlAddress(props?.hotelData?.hotel?.address?.country || '') ? (
              <>
                <span>{`${props?.hotelData?.hotel?.address?.addressLine1}`}</span>
                <span>{`${props?.hotelData?.hotel?.address?.city || ''}, ${
                  props?.hotelData?.hotel?.address?.state &&
                  props?.hotelData?.hotel?.address?.state !== 'NA'
                    ? props?.hotelData?.hotel?.address?.state
                    : ''
                } ${props?.hotelData?.hotel?.address?.postalCode || ''}`}</span>
                <span>{props?.hotelData?.hotel?.address?.countryName}</span>
              </>
            ) : (
              <>
                <span>{`${props?.hotelData?.hotel?.address?.addressLine1},`}</span>
                <span>{`${props?.hotelData?.hotel?.address?.city}, ${props?.hotelData?.hotel?.address?.state} ${props?.hotelData?.hotel?.address?.postalCode}`}</span>
                <span>{props?.hotelData?.hotel?.address?.countryName}</span>
              </>
            )}
          </p>
        </div>
        <div className="flex">
          <Icon name="telephone" size="md" aria-hidden={true} />
          <p className="ms-3.5 flex items-center">
            <span className="sr-only">{t('accessibility.phone')}</span>
            {getPhoneNumber(
              props?.hotelData?.hotel?.contactInfo?.phoneNumber || '',
              props?.hotelData?.hotel?.address?.country || ''
            )}
          </p>
        </div>
        {!props.isPreviewView ? (
          !props.isNoAvailError && !props.isAvailBellCurveError ? (
            <a
              className={cx(
                getButtonClasses('solid', 'lg', 'brand'),
                'text--xl inline-flex h-14 w-full max-w-[340px] items-center justify-center text-center'
              )}
              href={props?.bookURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('buttonsAndLinks.bookARoom')}{' '}
              <span className="sr-only">{t('accessibility.opensNewTab')}</span>
            </a>
          ) : null
        ) : (
          <Button fill="solid" size="lg" disabled={true} label={t('buttonsAndLinks.bookARoom')} />
        )}
      </div>
    </div>
  );
};

export default HotelInfo;
